import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { REIMBURSEMENT_METHODS, apiError, formatAmount, formatBalance, formatDay, minorToText, toMinor, todayIso } from "../../lib/finance";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const selectClass = "h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-base md:text-sm";

// Jamora pays back part or all of what it owes one payer, in one currency (balances = {currency: {remaining}}).
export default function ReimbursementDialog({ payer, payerLabel, balances, open, onOpenChange, onRecorded }) {
  const due = Object.entries(balances || {}).filter(([c, b]) => (toMinor(b.remaining, c) || 0) > 0);
  const [currency, setCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState(todayIso());
  const [method, setMethod] = useState("virement");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const key = useRef(null);

  const remainingOf = (c) => toMinor(balances?.[c]?.remaining, c) || 0;
  const pickCurrency = (c) => { setCurrency(c); setAmount(c ? minorToText(remainingOf(c), c).replace(".", ",") : ""); };

  useEffect(() => {
    if (!open) return;
    pickCurrency(due[0]?.[0] || "");
    setPaidOn(todayIso()); setMethod("virement"); setReference(""); setNote(""); setShowErrors(false);
    key.current = crypto.randomUUID(); // one key per opening: a double click cannot record the reimbursement twice
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- reset only when the dialog opens

  const minor = toMinor(amount, currency);
  const amountError = !minor ? "Indiquez un montant positif." : minor > remainingOf(currency) ? "Le montant dépasse le reste dû." : "";
  const dateError = !paidOn ? "Indiquez la date du remboursement." : paidOn > todayIso() ? "La date ne peut pas être dans le futur." : "";

  const save = async () => {
    setShowErrors(true);
    if (amountError || dateError || !currency) return;
    setSaving(true);
    try {
      await api.post("/finance/reimbursements", {
        payer, currency, amount: minorToText(minor, currency), paid_on: paidOn, method,
        reference: reference.trim() || null, note: note.trim() || null,
      }, { headers: { "Idempotency-Key": key.current } });
      toast.success(`Remboursement de ${formatAmount(minorToText(minor, currency), currency)} enregistré`);
      onRecorded();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="finance-reimbursement-dialog">
        <DialogHeader>
          <DialogTitle>Enregistrer un remboursement</DialogTitle>
          <DialogDescription>
            Ce que Jamora a rendu à {payerLabel}. Reste dû : <strong className="tabular-nums">{due.map(([c, b]) => formatBalance(b.remaining, c)).join(" · ")}</strong>.
            Un remboursement peut être partiel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {due.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="fin-reimb-currency">Devise</Label>
                <select id="fin-reimb-currency" value={currency} onChange={(e) => pickCurrency(e.target.value)} className={selectClass}>
                  {due.map(([c]) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="fin-reimb-amount">Montant ({currency})</Label>
              <Input id="fin-reimb-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="text-base md:text-sm tabular-nums" aria-invalid={showErrors && !!amountError} data-testid="finance-reimbursement-amount" />
              {showErrors && amountError && <p className="text-xs text-red-700">{amountError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="fin-reimb-date">Date du remboursement</Label>
              <Input id="fin-reimb-date" type="date" max={todayIso()} value={paidOn} onChange={(e) => setPaidOn(e.target.value)}
                className="text-base md:text-sm" aria-invalid={showErrors && !!dateError} data-testid="finance-reimbursement-date" />
              {showErrors && dateError && <p className="text-xs text-red-700">{dateError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="fin-reimb-method">Moyen</Label>
              <select id="fin-reimb-method" value={method} onChange={(e) => setMethod(e.target.value)} className={selectClass}
                data-testid="finance-reimbursement-method">
                {Object.entries(REIMBURSEMENT_METHODS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fin-reimb-ref">Référence</Label>
              <Input id="fin-reimb-ref" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120}
                placeholder="Ex. : n° de transaction" className="text-base md:text-sm" data-testid="finance-reimbursement-reference" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="fin-reimb-note">Note</Label>
            <Textarea id="fin-reimb-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={2}
              placeholder="Facultatif." className="text-base md:text-sm" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button type="button" onClick={save} disabled={saving} data-testid="finance-reimbursement-save">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer le remboursement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// A reimbursement recorded by mistake is cancelled with a reason: it stays listed, marked « Annulé », and stops counting.
export function CancelReimbursementDialog({ reimbursement, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [showError, setShowError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setReason(""); setShowError(false); }, [reimbursement?.id]);

  const confirm = async () => {
    if (!reason.trim()) { setShowError(true); return; }
    setSaving(true);
    try {
      await api.post(`/finance/reimbursements/${reimbursement.id}/cancel`, { expected_version: reimbursement.version, reason: reason.trim() });
      toast.success("Remboursement annulé");
      onCancelled();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AlertDialog open={!!reimbursement} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <AlertDialogContent data-testid="finance-cancel-reimbursement-dialog">
        {reimbursement && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Annuler ce remboursement ?</AlertDialogTitle>
              <AlertDialogDescription>
                {formatAmount(reimbursement.amount, reimbursement.currency)} le {formatDay(reimbursement.paid_on)}. Il restera visible, marqué « Annulé »,
                et la somme redeviendra due.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-2">
              <Label htmlFor="fin-cancel-reimb-reason">Motif de l'annulation *</Label>
              <Textarea id="fin-cancel-reimb-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3}
                placeholder="Ex. : mauvais montant, saisi deux fois" className="text-base md:text-sm"
                aria-invalid={showError && !reason.trim()} data-testid="finance-cancel-reimbursement-reason" />
              {showError && !reason.trim() && <p role="alert" className="text-xs text-red-700">Indiquez le motif de l'annulation.</p>}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>Retour</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white" data-testid="finance-cancel-reimbursement-confirm">
                {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Annuler le remboursement
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
